import { supabase, TABLES, BUCKETS } from '../lib/supabase';

class ArticleService {
  // Create a new article
  async createArticle(articleData, imageFile = null) {
    try {
      let imageUrl = '';
      
      if (imageFile) {
        imageUrl = await this.uploadImage(imageFile);
      }

      const { data, error } = await supabase
        .from(TABLES.ARTICLES)
        .insert([{
          ...articleData,
          image_url: imageUrl,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          views: 0,
          likes: 0,
          status: 'draft'
        }])
        .select()
        .single();

      if (error) throw error;
      return data.id;
    } catch (error) {
      console.error('Error creating article:', error);
      throw error;
    }
  }

  // Update an existing article
  async updateArticle(id, articleData, imageFile = null) {
    try {
      const updateData = {
        ...articleData,
        updated_at: new Date().toISOString()
      };

      if (imageFile) {
        // Delete old image if exists
        const oldArticle = await this.getArticle(id);
        if (oldArticle.image_url) {
          await this.deleteImage(oldArticle.image_url);
        }
        
        updateData.image_url = await this.uploadImage(imageFile);
      }

      const { data, error } = await supabase
        .from(TABLES.ARTICLES)
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating article:', error);
      throw error;
    }
  }

  // Get a single article by ID
  async getArticle(id) {
    try {
      const { data, error } = await supabase
        .from(TABLES.ARTICLES)
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error getting article:', error);
      throw error;
    }
  }

  // Get articles with filters and pagination
  async getArticles(filters = {}) {
    try {
      let query = supabase.from(TABLES.ARTICLES).select('*');
      
      // Apply filters
      if (filters.section) {
        query = query.eq('section', filters.section);
      }
      
      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      
      if (filters.author) {
        query = query.eq('author', filters.author);
      }

      // Apply ordering
      const orderBy = filters.orderBy || 'created_at';
      const order = filters.order || 'desc';
      query = query.order(orderBy, { ascending: order === 'asc' });
      
      // Apply limit
      if (filters.limit) {
        query = query.limit(filters.limit);
      }

      // Apply pagination
      if (filters.offset) {
        query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1);
      }

      const { data, error } = await query;
      if (error) throw error;
      
      return data || [];
    } catch (error) {
      console.error('Error getting articles:', error);
      throw error;
    }
  }

  // Get published articles for public view
  async getPublishedArticles(section = null, limitCount = 10) {
    try {
      const filters = {
        status: 'published',
        limit: limitCount,
        orderBy: 'published_at'
      };
      
      if (section) {
        filters.section = section;
      }

      return await this.getArticles(filters);
    } catch (error) {
      console.error('Error getting published articles:', error);
      throw error;
    }
  }

  // Get featured articles
  async getFeaturedArticles(limitCount = 3) {
    try {
      const { data, error } = await supabase
        .from(TABLES.ARTICLES)
        .select('*')
        .eq('status', 'published')
        .eq('featured', true)
        .order('published_at', { ascending: false })
        .limit(limitCount);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting featured articles:', error);
      throw error;
    }
  }

  // Delete an article
  async deleteArticle(id) {
    try {
      const article = await this.getArticle(id);
      
      // Delete associated image
      if (article.image_url) {
        await this.deleteImage(article.image_url);
      }

      const { error } = await supabase
        .from(TABLES.ARTICLES)
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting article:', error);
      throw error;
    }
  }

  // Upload image to Supabase Storage
  async uploadImage(file) {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `articles/${fileName}`;
      
      const { error } = await supabase.storage
        .from(BUCKETS.ARTICLE_IMAGES)
        .upload(filePath, file);

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from(BUCKETS.ARTICLE_IMAGES)
        .getPublicUrl(filePath);
      
      return publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  }

  // Delete image from Supabase Storage
  async deleteImage(imageUrl) {
    try {
      // Extract file path from URL
      const urlParts = imageUrl.split('/');
      const fileName = urlParts[urlParts.length - 1];
      const filePath = `articles/${fileName}`;
      
      const { error } = await supabase.storage
        .from(BUCKETS.ARTICLE_IMAGES)
        .remove([filePath]);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting image:', error);
      // Don't throw error for image deletion failures
    }
  }

  // Increment article views
  async incrementViews(id) {
    try {
      const { error } = await supabase.rpc('increment_article_views', {
        article_id: id
      });
      
      if (error) throw error;
    } catch (error) {
      console.error('Error incrementing views:', error);
    }
  }

  // Toggle article like
  async toggleLike(id, userId) {
    try {
      const { error } = await supabase.rpc('toggle_article_like', {
        article_id: id,
        user_id: userId
      });
      
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error toggling like:', error);
      throw error;
    }
  }

  // Search articles
  async searchArticles(searchTerm, section = null) {
    try {
      let query = supabase
        .from(TABLES.ARTICLES)
        .select('*')
        .eq('status', 'published')
        .or(`title.ilike.%${searchTerm}%,content.ilike.%${searchTerm}%,subtitle.ilike.%${searchTerm}%`)
        .order('published_at', { ascending: false });

      if (section) {
        query = query.eq('section', section);
      }

      const { data, error } = await query;
      if (error) throw error;
      
      return data || [];
    } catch (error) {
      console.error('Error searching articles:', error);
      throw error;
    }
  }
}

const articleService = new ArticleService();
export default articleService;